// shared/mastery — secret: fame curve, fame-→-mastery-level mapping, paragon multiplier.
// Phase 2 ships an accumulator + linear curve; Phase 3 will swap to the real curve.

export type FamePoints = number;

export type FameAction = 'gather' | 'refine' | 'craft' | 'combat' | 'boss';

export interface FameSource {
  itemDefId: string;
  action: FameAction;
}

const ACTION_BASE_FAME: Record<FameAction, number> = {
  gather: 4,
  refine: 6,
  craft: 12,
  combat: 8,
  boss: 50,
};

export function gainFor(source: FameSource): FamePoints {
  return ACTION_BASE_FAME[source.action] ?? 0;
}

// Phase 2 placeholder curve: every 200 fame = 1 level, capped at 99.
const FAME_PER_LEVEL = 200;
const MAX_LEVEL = 99;

export function levelFor(fame: FamePoints): number {
  if (fame <= 0) return 0;
  return Math.min(MAX_LEVEL, Math.floor(fame / FAME_PER_LEVEL));
}

export function fameToNextLevel(fame: FamePoints): { level: number; into: number; need: number } {
  const level = levelFor(fame);
  if (level >= MAX_LEVEL) {
    return { level, into: FAME_PER_LEVEL, need: FAME_PER_LEVEL };
  }
  const into = fame - level * FAME_PER_LEVEL;
  return { level, into, need: FAME_PER_LEVEL };
}

// Mastery bonus per level: 0.5% per level (additive multiplier, applied where the caller cares).
export function masteryBonus(level: number): number {
  return Math.max(0, Math.min(MAX_LEVEL, level)) * 0.005;
}

export function paragonMultiplier(_prevSeasonFame: FamePoints): number {
  throw new Error('shared/mastery.paragonMultiplier: not implemented (Phase 6)');
}
